import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
} from 'recharts'
import {
  Calendar,
  DollarSign,
  Users,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { Button } from '@components/ui/button'
import { useAnalyticsOverview } from '@hooks/useAnalytics'
import { cn } from '@lib/utils'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface KPICardProps {
  title: string
  value: number
  changePercent?: number
  changeLabel?: string
  icon: React.ElementType
  color: string
  format?: 'number' | 'currency' | 'percent'
}

function KPICard({ title, value, changePercent, changeLabel, icon: Icon, color, format = 'number' }: KPICardProps) {
  const displayValue = format === 'currency' 
    ? formatCurrency(value)
    : format === 'percent'
    ? `${value.toFixed(1)}%`
    : value.toLocaleString()

  const TrendIcon = changePercent === undefined ? Minus : changePercent >= 0 ? TrendingUp : TrendingDown
  const trendColor = changePercent === undefined ? 'text-gray-500' : changePercent >= 0 ? 'text-emerald-600' : 'text-red-600'

  return (
    <Card className="healthcare-card">
      <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
        <div className="space-y-1">
          <CardDescription className="text-sm font-medium text-muted-foreground">
            {title}
          </CardDescription>
          <CardTitle className="text-2xl font-bold text-foreground">
            {displayValue}
          </CardTitle>
          {changePercent !== undefined && (
            <div className={cn("flex items-center gap-1 text-xs", trendColor)}>
              <TrendIcon className="h-3 w-3" />
              <span>{Math.abs(changePercent).toFixed(1)}%</span>
              {changeLabel && <span className="text-muted-foreground">{changeLabel}</span>}
            </div>
          )}
        </div>
        <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </CardHeader>
    </Card>
  )
}

export function DepartmentAnalyticsPage() {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d')
  
  const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
  const endDate = new Date().toISOString().split('T')[0]
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const { data, isLoading } = useAnalyticsOverview({ start_date: startDate, end_date: endDate })

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Analíticas de Departamento</h1>
            <p className="text-muted-foreground mt-1">Cargando datos de analíticas...</p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="healthcare-card h-32">
              <CardContent className="p-6">
                <div className="animate-pulse bg-muted h-4 w-24 rounded mb-2" />
                <div className="animate-pulse bg-muted h-8 w-32 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const kpiData = data?.kpi_summary
  const appointmentData = data?.appointments
  const revenueData = data?.revenue
  const patientData = data?.patients
  const departmentData = data?.departments

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Analíticas de Departamento</h1>
          <p className="text-muted-foreground mt-1">
            Métricas e insights en tiempo real para la eficiencia operativa.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={dateRange === '7d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('7d')}
          >
            7 Días
          </Button>
          <Button
            variant={dateRange === '30d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('30d')}
          >
            30 Días
          </Button>
          <Button
            variant={dateRange === '90d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('90d')}
          >
            90 Días
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <section aria-label="Indicadores Clave de Desempeño">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {kpiData?.kpi_cards.map((card, index) => (
            <KPICard
              key={card.title}
              title={card.title}
              value={card.value}
              changePercent={card.change_percent}
              changeLabel={card.change_label}
              icon={[Calendar, DollarSign, Users, Activity][index]}
              color={['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500'][index]}
              format={index === 1 ? 'currency' : 'number'}
            />
          ))}
        </div>
      </section>

      {/* Charts Row 1 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Appointment Trends */}
        <Card className="healthcare-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-medical-600" />
              Tendencias de Citas
            </CardTitle>
            <CardDescription>Volumen diario de citas por estado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={appointmentData?.daily_trends}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(label) => formatDate(label as string)}
                    contentStyle={{ borderRadius: 8 }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="completed"
                    stackId="1"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="Completadas"
                  />
                  <Area
                    type="monotone"
                    dataKey="scheduled"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="Programadas"
                  />
                  <Area
                    type="monotone"
                    dataKey="cancelled"
                    stackId="1"
                    stroke="#ef4444"
                    fill="#ef4444"
                    fillOpacity={0.6}
                    name="Canceladas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Trends */}
        <Card className="healthcare-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-medical-600" />
              Tendencias de Ingresos
            </CardTitle>
            <CardDescription>Ingresos diarios y saldo pendiente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData?.daily_trends}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    labelFormatter={(label) => formatDate(label as string)}
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: 8 }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total_revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                    name="Ingresos"
                  />
                  <Line
                    type="monotone"
                    dataKey="outstanding_amount"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={false}
                    name="Pendiente"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Appointments by Status */}
        <Card className="healthcare-card">
          <CardHeader>
            <CardTitle>Citas por Estado</CardTitle>
            <CardDescription>Distribución de estados de citas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={appointmentData?.by_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="status"
                  >
                    {appointmentData?.by_status.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Department */}
        <Card className="healthcare-card">
          <CardHeader>
            <CardTitle>Ingresos por Departamento</CardTitle>
            <CardDescription>Distribución de ingresos por departamento</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData?.by_department.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    type="number"
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="department"
                    tick={{ fontSize: 12 }}
                    width={100}
                  />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Bar dataKey="total_revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Patient Demographics */}
        <Card className="healthcare-card">
          <CardHeader>
            <CardTitle>Demografía de Pacientes</CardTitle>
            <CardDescription>Distribución por grupo de edad</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={patientData?.demographics}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="age_group" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Performance Table */}
      <Card className="healthcare-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-medical-600" />
            Desempeño por Departamento
          </CardTitle>
          <CardDescription>
            Mejor desempeño: <span className="font-medium text-emerald-600">{departmentData?.top_performing}</span>
            {departmentData?.needs_attention.length ? (
              <span className="ml-4">
                Requiere atención: {departmentData.needs_attention.join(', ')}
              </span>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Departamento</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Citas</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Ingresos</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Pacientes</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Utilización</th>
                </tr>
              </thead>
              <tbody>
                {departmentData?.departments.map((dept) => (
                  <tr key={dept.department} className="border-b border-border/50">
                    <td className="py-3 px-4 font-medium">{dept.department}</td>
                    <td className="text-right py-3 px-4">{dept.appointment_count.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">{formatCurrency(dept.revenue)}</td>
                    <td className="text-right py-3 px-4">{dept.patient_count.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-medium",
                        dept.utilization_rate >= 80 ? "bg-emerald-100 text-emerald-700" :
                        dept.utilization_rate >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      )}>
                        {dept.utilization_rate.toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
